/**
 * Sprinkler Cost Calculator engine — pure functions, no side effects.
 *
 * Turns (lawn size, pro vs DIY, region) into an installed-cost estimate:
 * typical cost with a low–high range, zone count, per-zone cost, and a
 * where-the-money-goes breakdown. Figures are 2026 national-average
 * homeowner numbers, rounded to the nearest $50.
 */

export type InstallType = 'pro' | 'diy'
export type SprinklerRegion = 'north' | 'transition' | 'south'

export interface SprinklerCostInput {
  areaSqFt: number
  install: InstallType
  region: SprinklerRegion
  /** Sq ft one zone covers; defaults to 1,500 (typical mixed spray/rotor) */
  sqftPerZone?: number
}

export interface SprinklerBreakdownLine {
  id: string
  label: string
  cost: number
  costLabel: string
}

export interface SprinklerCostResult {
  zones: number
  typical: number
  low: number
  high: number
  perZone: number
  typicalLabel: string
  lowLabel: string
  highLabel: string
  perZoneLabel: string
  breakdown: SprinklerBreakdownLine[]
  regionNote: string
  tip: string
  summary: string
  areaLabel: string
  /** Installed cost per sq ft, for sanity-checking against quotes */
  perSqFt: number
  perSqFtLabel: string
}

/** Zone coverage options for the advanced section. Spray heads cover less
 *  area per zone than rotors at the same supply flow. */
export const ZONE_COVERAGE_OPTIONS = [
  { sqft: 1200, label: 'Spray-heavy', sub: 'small or cut-up yard' },
  { sqft: 1500, label: 'Typical mix', sub: 'sprays + rotors' },
  { sqft: 2000, label: 'Rotor-heavy', sub: 'large open lawn' },
] as const

export const SPRINKLER_REGIONS: Record<
  SprinklerRegion,
  { label: string; sub: string; mult: number; note: string }
> = {
  north: {
    label: 'North',
    sub: 'hard freezes',
    mult: 1.15,
    note: 'Freezing climates need annual winterization blowouts and freeze-protected backflow hardware, which adds cost up north.',
  },
  transition: {
    label: 'Transition',
    sub: 'mixed climate',
    mult: 1.0,
    note: 'Moderate trench depth and winterization needs. Costs sit near the national average.',
  },
  south: {
    label: 'South',
    sub: 'no hard freeze',
    mult: 0.9,
    note: 'No winterization and simpler freeze protection keep installs cheaper in the south.',
  },
}

export const SPRINKLER_SIZE_PRESETS = [5000, 8000, 12000, 21780] as const

/** Typical sq ft covered by one irrigation zone at residential pressure */
const SQFT_PER_ZONE = 1500

/** Round to the nearest $50 and format with thousands separators */
function money(v: number): string {
  return '$' + (Math.round(v / 50) * 50).toLocaleString('en-US')
}

export function calculateSprinklerCost(input: SprinklerCostInput): SprinklerCostResult {
  const areaSqFt = Number.isFinite(input.areaSqFt) ? Math.max(input.areaSqFt, 0) : 0
  const region = SPRINKLER_REGIONS[input.region] ? input.region : 'transition'
  const pro = input.install !== 'diy'

  const sqftPerZone =
    input.sqftPerZone && input.sqftPerZone > 0 ? input.sqftPerZone : SQFT_PER_ZONE
  // 0 sq ft is "no lawn entered", not a one-zone system.
  const zones = areaSqFt > 0 ? Math.max(1, Math.ceil(areaSqFt / sqftPerZone)) : 0
  const mult = SPRINKLER_REGIONS[region].mult
  const perZone = (pro ? 775 : 300) * mult
  const base = (pro ? 400 : 250) * mult
  const typical = zones > 0 ? zones * perZone + base : 0
  const low = typical * 0.78
  const high = typical * 1.3

  const lines: Array<{ id: string; label: string; share: number }> = pro
    ? [
        { id: 'labor', label: 'Labor and trenching', share: 0.45 },
        { id: 'heads', label: 'Sprinkler heads', share: 0.2 },
        { id: 'valves', label: 'Valves and manifold', share: 0.15 },
        { id: 'controller', label: 'Smart controller', share: 0.1 },
        { id: 'backflow', label: 'Backflow preventer + permit', share: 0.1 },
      ]
    : [
        // Shares sum to 1.0. The source design's DIY shares summed to 1.3 —
        // a breakdown exceeding its own total — so they're renormalized here.
        { id: 'pipe', label: 'Pipe, fittings, trenching rental', share: 0.3 },
        { id: 'heads', label: 'Sprinkler heads', share: 0.25 },
        { id: 'valves', label: 'Valves and manifold', share: 0.2 },
        { id: 'controller', label: 'Smart controller', share: 0.15 },
        { id: 'backflow', label: 'Backflow preventer', share: 0.1 },
      ]

  const breakdown: SprinklerBreakdownLine[] = lines.map((line) => {
    const cost = typical * line.share
    return { id: line.id, label: line.label, cost, costLabel: money(cost) }
  })

  return {
    zones,
    typical,
    low,
    high,
    perZone,
    typicalLabel: money(typical),
    lowLabel: money(low),
    highLabel: money(high),
    perZoneLabel: money(perZone),
    breakdown,
    regionNote: SPRINKLER_REGIONS[region].note,
    tip: pro
      ? 'Get 3 quotes and ask each installer to price the same zone count. Quotes for identical yards commonly spread by 40%.'
      : 'Rent a pipe puller instead of trenching. It halves the labor and your lawn recovers in two weeks instead of a season.',
    summary: `${areaSqFt.toLocaleString('en-US')} sq ft · ${pro ? 'pro install' : 'DIY'}`,
    areaLabel: areaSqFt.toLocaleString('en-US'),
    perSqFt: areaSqFt > 0 ? typical / areaSqFt : 0,
    perSqFtLabel: areaSqFt > 0 ? `$${(typical / areaSqFt).toFixed(2)}` : '$0.00',
  }
}
